/**
 * <safe-sticky> - a "smart" sticky element.
 *
 * Unlike plain `position: sticky`, this clamps the element's top offset based
 * on scroll direction so tall content (taller than the viewport) scrolls
 * naturally instead of being pinned and cutting off content, while still
 * respecting the theme's sticky header height.
 *
 * Usage:
 *   <safe-sticky offset="30">...</safe-sticky>
 *
 * The `offset` attribute (optional, px) is added on top of the current
 * sticky header height to determine the initial top offset.
 */
var SafeSticky = class extends HTMLElement {
  connectedCallback() {
    if (this.classList.contains('safe-sticky--disabled')) {
      return;
    }
    this.lastKnownY = window.scrollY;
    this.currentTop = 0;
    this.hasPendingRaf = false;
    this._boundCheckPosition = this._checkPosition.bind(this);
    window.addEventListener('scroll', this._boundCheckPosition, { passive: true });
    window.addEventListener('resize', this._boundCheckPosition);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._boundCheckPosition);
    window.removeEventListener('resize', this._boundCheckPosition);
  }

  get initialTopOffset() {
    const stickyHeaderHeight = (window.theme && typeof theme.stickyHeaderHeight === 'function')
      ? theme.stickyHeaderHeight()
      : 0;
    return stickyHeaderHeight + (parseInt(this.getAttribute('offset'), 10) || 0);
  }

  _checkPosition() {
    if (this.hasPendingRaf) {
      return;
    }
    this.hasPendingRaf = true;
    requestAnimationFrame(() => {
      const bounds = this.getBoundingClientRect();
      const maxTop = bounds.top + window.scrollY - this.offsetTop + this.initialTopOffset;
      const minTop = this.clientHeight - window.innerHeight;

      if (window.scrollY < this.lastKnownY) {
        this.currentTop -= window.scrollY - this.lastKnownY;
      } else {
        this.currentTop += this.lastKnownY - window.scrollY;
      }

      this.currentTop = Math.min(Math.max(this.currentTop, -minTop), maxTop, this.initialTopOffset);
      this.lastKnownY = window.scrollY;
      this.style.top = `${this.currentTop}px`;
      this.hasPendingRaf = false;
    });
  }
};

if (!window.customElements.get('safe-sticky')) {
  window.customElements.define('safe-sticky', SafeSticky);
}
